import type { LectureCompteur, MouvementCompteur, MouvementSalarie } from '../../services/conges-api';

/**
 * Les briques d'affichage des compteurs, partagées par « Mes congés » (vue du
 * salarié) et l'onglet de pilotage « Compteurs » (vue RH, R-CPT-14).
 *
 * Extraites de `MesCongesPage` : le dictionnaire des compteurs y était déjà
 * recopié dans le détail d'une demande, et la vue RH en aurait fait un
 * troisième exemplaire. Trois copies d'un libellé, ce sont trois occasions de
 * les voir diverger.
 */

/** Format court : 12 et non 12,0 ; 12,5 et non 12.5. */
export function jours(v: number): string {
  return v.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
}

export const LIBELLE_COMPTEUR: Record<string, string> = {
  CP_N1: 'CP reliquat N-1',
  CP_N: 'CP année en cours',
  RTT: 'RTT',
  EXAMENS: 'Examens',
  ABS_AUT: 'Absence autorisée',
};

export const LIBELLE_ORIGINE: Record<string, string> = {
  acquisition_mensuelle: 'Acquisition du mois',
  conges_anciennete: 'Congés d’ancienneté',
  demande_approuvee: 'Absence décomptée',
  annulation: 'Annulation d’absence',
  recalcul_rtt: 'Recalcul des RTT',
  correction_manuelle: 'Correction',
  bascule_annuelle: 'Bascule du 1er janvier',
  initialisation_migration: 'Reprise Airtable',
};

export function dateFr(iso: string): string {
  const [a, m, j] = iso.slice(0, 10).split('-');
  return `${j}/${m}/${a}`;
}

/** Une carte de compteur, en trois lectures (R-CPT-11). */
export function CarteCompteur({ c }: { c: LectureCompteur }) {
  const enAttente = c.en_attente > 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-sm font-medium text-slate-600">{c.libelle}</p>

      {/* ③ Le prévisionnel, en avant : c'est la réponse à la vraie question. */}
      <p className="mt-2 text-3xl font-semibold text-slate-900">
        {jours(c.previsionnel)}
        <span className="ml-1 text-base font-normal text-slate-500">j</span>
      </p>
      <p className="text-xs text-slate-500">
        {enAttente ? 'Prévisionnel : s’il valide tout ce que vous avez posé' : 'Disponible'}
      </p>

      {/* ① et ② en dessous, en petit : le détail de ce qui compose le chiffre. */}
      <dl className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-slate-500">Solde aujourd’hui</dt>
          <dd className="tabular-nums text-slate-700">{jours(c.courant)} j</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-500">En attente de validation</dt>
          <dd className={`tabular-nums ${enAttente ? 'text-amber-700' : 'text-slate-400'}`}>
            {enAttente ? `− ${jours(c.en_attente)} j` : '-'}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * La chronologie des mouvements d'un compteur.
 *
 * C'est ce qui permet de comprendre POURQUOI un solde vaut ce qu'il vaut, et de
 * le contester s'il se trompe. La RH y voit en plus l'auteur d'une correction :
 * un compteur corrigé sans qu'on sache par qui ne se défend pas.
 */
export function ChronologieCompteurs({
  mouvements,
  avecAuteur = false,
}: {
  mouvements: Array<MouvementCompteur | MouvementSalarie>;
  avecAuteur?: boolean;
}) {
  if (mouvements.length === 0) {
    return (
      <p className="rounded-lg border border-slate-200 bg-white p-4 text-slate-500">
        Aucun mouvement pour l’instant.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-left text-slate-600">
          <tr>
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium">Compteur</th>
            <th className="px-3 py-2 text-right font-medium">Jours</th>
            <th className="px-3 py-2 font-medium">Motif</th>
            {avecAuteur && <th className="px-3 py-2 font-medium">Par</th>}
          </tr>
        </thead>
        <tbody>
          {[...mouvements].reverse().map((m) => (
            <tr key={m.id} className="border-t border-slate-100">
              <td className="whitespace-nowrap px-3 py-2 text-slate-600">{dateFr(m.date_effet)}</td>
              <td className="px-3 py-2 text-slate-700">
                {LIBELLE_COMPTEUR[m.compteur] ?? m.compteur}
              </td>
              <td
                className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                  m.jours < 0 ? 'text-red-700' : 'text-emerald-700'
                }`}
              >
                {m.jours > 0 ? '+' : ''}
                {jours(m.jours)}
              </td>
              <td className="px-3 py-2 text-slate-600">
                {LIBELLE_ORIGINE[m.origine] ?? m.origine}
                {m.commentaire ? ` (${m.commentaire})` : ''}
              </td>
              {avecAuteur && (
                <td className="px-3 py-2 text-slate-500">
                  {'auteur_email' in m ? (m.auteur_email ?? '') : ''}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
